import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Badge, Select } from '../components/Common';
import { Calendar } from '../components/Calendar/Calendar';
import { useCancellations, useCreateCancellation } from '../hooks/useCancellations';
import { usePatients } from '../hooks/usePatients';
import { usePhysios } from '../hooks/usePhysios';
import { useAppointments } from '../hooks/useAppointments';
import type { Cancellation, Appointment } from '../types';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusBadge(status: Cancellation['status']) {
  const variants = {
    pending: 'warning' as const,
    resolved: 'success' as const,
    failed: 'danger' as const,
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}

// Get today's date in YYYY-MM-DD format for default value
function getDefaultDate(): string {
  const now = new Date();
  return now.toISOString().slice(0, 10);
}

// Get current time rounded to nearest 30 min
function getDefaultTime(): string {
  const now = new Date();
  const minutes = now.getMinutes() >= 30 ? '30' : '00';
  const hours = now.getHours().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function Dashboard() {
  const { data: cancellations, isLoading } = useCancellations();
  const { data: patients, isLoading: patientsLoading } = usePatients();
  const { data: physios, isLoading: physiosLoading } = usePhysios();
  const { data: appointments } = useAppointments();
  const createCancellation = useCreateCancellation();

  const [showForm, setShowForm] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [formData, setFormData] = useState({
    patient_id: '',
    date: getDefaultDate(),
    time: getDefaultTime(),
    reason: '',
    reported_by: '',
  });

  const pendingCancellations = cancellations?.filter((c) => c.status === 'pending') || [];
  const recentCancellations = cancellations?.slice(0, 5) || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.patient_id || !formData.date || !formData.time) return;

    try {
      const originalTime = new Date(`${formData.date}T${formData.time}`).toISOString();
      await createCancellation.mutateAsync({
        patient_id: parseInt(formData.patient_id),
        original_time: originalTime,
        reason: formData.reason || undefined,
        reported_by: formData.reported_by || undefined,
      });
      setFormData({
        patient_id: '',
        date: getDefaultDate(),
        time: getDefaultTime(),
        reason: '',
        reported_by: '',
      });
      setShowForm(false);
    } catch (error) {
      console.error('Failed to create cancellation:', error);
    }
  };

  // Generate time slots for the dropdown
  const timeSlots = [];
  for (let h = 8; h <= 18; h++) {
    for (const m of ['00', '30']) {
      const hour = h.toString().padStart(2, '0');
      timeSlots.push({ value: `${hour}:${m}`, label: `${hour}:${m}` });
    }
  }

  // Map appointments with patient and physio names
  const appointmentsWithNames = (appointments || []).map((apt) => ({
    ...apt,
    patient_name: patients?.find((p) => p.id === apt.patient_id)?.name || 'Unknown Patient',
    physio_name: physios?.find((p) => p.id === apt.physio_id)?.name || null,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Manage cancellations and find replacements</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className={`w-full sm:w-auto ${showForm ? 'bg-gray-500 hover:bg-gray-600' : ''}`}
        >
          {showForm ? (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Report Cancellation
            </>
          )}
        </Button>
      </div>

      {/* Quick Entry Form */}
      {showForm && (
        <Card title="Report New Cancellation" className="border-2 border-primary-100 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Patient Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient <span className="text-red-500">*</span>
                </label>
                {patientsLoading ? (
                  <div className="h-10 bg-gray-100 animate-pulse rounded-lg" />
                ) : (
                  <select
                    value={formData.patient_id}
                    onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                    required
                  >
                    <option value="">Select a patient...</option>
                    {patients?.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} {p.postcode ? `(${p.postcode})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Date and Time in a row */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                    required
                  >
                    {timeSlots.map((slot) => (
                      <option key={slot.value} value={slot.value}>
                        {slot.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reason */}
              <Select
                label="Reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                options={[
                  { value: '', label: 'Select a reason...' },
                  { value: 'Illness', label: 'Patient Illness' },
                  { value: 'Emergency', label: 'Family Emergency' },
                  { value: 'Transport', label: 'Transport Issues' },
                  { value: 'Weather', label: 'Bad Weather' },
                  { value: 'Carer Unavailable', label: 'Carer Unavailable' },
                  { value: 'Hospital Appointment', label: 'Hospital Appointment' },
                  { value: 'Other', label: 'Other' },
                ]}
              />

              {/* Reported By - Now a physio dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reported By (Physio)
                </label>
                {physiosLoading ? (
                  <div className="h-10 bg-gray-100 animate-pulse rounded-lg" />
                ) : (
                  <select
                    value={formData.reported_by}
                    onChange={(e) => setFormData({ ...formData, reported_by: e.target.value })}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  >
                    <option value="">Select reporting physio...</option>
                    {physios?.map((p) => (
                      <option key={p.id} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
              <Button variant="secondary" type="button" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={createCancellation.isPending}>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Submit Cancellation
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Pending Cancellations and Recent Activity - Most Important */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Cancellations */}
        <Card
          title="Pending Cancellations"
          actions={
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              View all →
            </Link>
          }
        >
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : pendingCancellations.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500">No pending cancellations</p>
              <p className="text-sm text-gray-400 mt-1">All caught up!</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {pendingCancellations.map((cancellation) => (
                <Link
                  key={cancellation.id}
                  to={`/cancellations/${cancellation.id}`}
                  className="block p-3 bg-gray-50 rounded-lg hover:bg-primary-50 border border-transparent hover:border-primary-200 transition-all duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <span className="text-primary-700 font-semibold text-xs">
                          {cancellation.patient_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{cancellation.patient_name}</p>
                        <p className="text-xs text-gray-500">
                          {formatDate(cancellation.original_time)}
                        </p>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        {/* Recent Activity */}
        <Card title="Recent Activity">
          {recentCancellations.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No recent activity</p>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
              {recentCancellations.map((cancellation) => (
                <div key={cancellation.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        cancellation.status === 'pending'
                          ? 'bg-amber-500'
                          : cancellation.status === 'resolved'
                            ? 'bg-emerald-500'
                            : 'bg-red-500'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {cancellation.patient_name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(cancellation.created_at)}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(cancellation.status)}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-amber-700">Pending</p>
              <p className="text-2xl font-bold text-amber-800 mt-1">{pendingCancellations.length}</p>
            </div>
            <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-primary-700">Patients</p>
              <p className="text-2xl font-bold text-primary-800 mt-1">{patients?.length || 0}</p>
            </div>
            <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-700">Physios</p>
              <p className="text-2xl font-bold text-blue-800 mt-1">{physios?.length || 0}</p>
            </div>
            <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          </div>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-700">Appointments</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1">{appointments?.length || 0}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-200 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      {/* Appointment Calendar */}
      <Calendar
        appointments={appointmentsWithNames}
        physios={physios || []}
        patients={patients || []}
        onAppointmentClick={(apt) => setSelectedAppointment(apt)}
      />

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Appointment Details</h3>
              <button
                onClick={() => setSelectedAppointment(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Patient</p>
                <p className="font-medium">{selectedAppointment.patient_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Physio</p>
                <p className="font-medium">{selectedAppointment.physio_name || 'Unassigned'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p className="font-medium">{formatDate(selectedAppointment.start_time)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={selectedAppointment.status === 'scheduled' ? 'info' : selectedAppointment.status === 'completed' ? 'success' : 'default'}>
                  {selectedAppointment.status}
                </Badge>
              </div>
              {selectedAppointment.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-sm">{selectedAppointment.notes}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <Button variant="secondary" onClick={() => setSelectedAppointment(null)}>
                Close
              </Button>
              <Button
                onClick={() => {
                  // Pre-fill cancellation form
                  setFormData({
                    patient_id: selectedAppointment.patient_id.toString(),
                    date: selectedAppointment.start_time.split('T')[0],
                    time: selectedAppointment.start_time.split('T')[1].slice(0, 5),
                    reason: '',
                    reported_by: selectedAppointment.physio_name || '',
                  });
                  setShowForm(true);
                  setSelectedAppointment(null);
                }}
              >
                Report Cancellation
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
